import localforage from 'localforage'

localforage.config({
  name: 'one-task-view',
  storeName: 'storage',
  version: 1,
  description: 'ONE-TASK-VIEW local storage',
})

export const STORAGE_KEYS = {
  SESSIONS: 'sessions',
  TILES: 'tiles',
  ANSWERS: 'answers',
  HISTORY_STACKS: 'historyStacks',
  IMPORT_JOBS: 'importJobs',
  EXPORT_JOBS: 'exportJobs',
  SYNC_RECORDS: 'syncRecords',
  AUTO_SYNC_SETTINGS: 'autoSyncSettings',
  MIGRATION_VERSION: 'migrationVersion',
  LAST_SESSION_ID: 'lastSessionId',
  HERMES_GATEWAY_URL: 'hermesGatewayUrl',
} as const

export type TileStatus = 'pending' | 'active' | 'answered' | 'recalculating'
export type SessionStatus = 'active' | 'paused' | 'completed' | 'archived'
export type AnswerType = 'suggestion' | 'freetext'
export type ImportSource = 'manual' | 'markdown' | 'hermes-text' | 'project-files' | 'n8n' | 'external-task-tools' | 'csv' | 'json'
export type ImportJobStatus = 'pending' | 'processing' | 'completed' | 'failed'
export type ExportJobStatus = 'pending' | 'processing' | 'completed' | 'failed'
export type SyncState = 'synced' | 'pending' | 'conflict' | 'error'

export interface Session {
  id: string
  taskId: string
  title: string
  activeIndex: number
  status: SessionStatus
  syncState: SyncState
  createdAt: number
  updatedAt: number
  completedAt?: number
  totalSteps: number
  currentStep: number
  metadata?: Record<string, unknown>
}

export interface Tile {
  id: string
  sessionId: string
  index: number
  question: string
  suggestions: string[]
  answer?: string
  answerType?: AnswerType
  status: TileStatus
  safetyLevel: 'safe' | 'caution' | 'critical'
  createdAt: number
  updatedAt: number
  answeredAt?: number
  isRecalculating: boolean
  dependsOnPrevious: boolean
  metadata?: Record<string, unknown>
}

export interface Answer {
  id: string
  tileId: string
  sessionId: string
  value: string
  type: AnswerType
  previousValue?: string
  isRecalculated: boolean
  createdAt: number
  updatedAt: number
}

export interface HistoryStack {
  id: string
  sessionId: string
  tileId: string
  answers: Answer[]
  currentIndex: number
  maxSize: number
  createdAt: number
  updatedAt: number
}

export interface ImportJob {
  id: string
  source: ImportSource
  sourceData: string
  status: ImportJobStatus
  parsedTasks: ParsedTask[]
  errors: string[]
  createdAt: number
  updatedAt: number
  completedAt?: number
  sessionId?: string
}

export interface ParsedTask {
  title: string
  source: ImportSource
  projectPath?: string
  score?: number
  status?: string
  dependencies?: string[]
  metadata?: Record<string, unknown>
}

export interface ExportJob {
  id: string
  sessionId: string
  format: 'json' | 'markdown'
  data: string
  status: ExportJobStatus
  createdAt: number
  updatedAt: number
  completedAt?: number
  filePath?: string
}

export interface SyncRecord {
  id: string
  source: string
  target: string
  time: number
  summary: string
  result: 'success' | 'failed' | 'partial'
  details?: Record<string, unknown>
}

export interface AutoSyncSettings {
  enabled: boolean
  intervalMs: number
  lastSyncAt?: number
}

const CURRENT_MIGRATION_VERSION = 1

async function getMigrationVersion(): Promise<number> {
  const version = await localforage.getItem<number>(STORAGE_KEYS.MIGRATION_VERSION)
  return version ?? 0
}

async function setMigrationVersion(version: number): Promise<void> {
  await localforage.setItem(STORAGE_KEYS.MIGRATION_VERSION, version)
}

export async function runMigrations(): Promise<void> {
  const currentVersion = await getMigrationVersion()

  if (currentVersion < 1) {
    await migrateToV1()
    await setMigrationVersion(1)
  }
}

async function migrateToV1(): Promise<void> {
  const sessions = await getAllSessions()
  for (const session of sessions) {
    if (!session.syncState) {
      session.syncState = 'pending'
      await saveSession(session)
    }
    if (!session.metadata) {
      session.metadata = {}
      await saveSession(session)
    }
  }

  const tiles = await getAllTiles()
  for (const tile of tiles) {
    if (!tile.isRecalculating) {
      tile.isRecalculating = false
      await saveTile(tile)
    }
    if (!tile.dependsOnPrevious) {
      tile.dependsOnPrevious = true
      await saveTile(tile)
    }
  }
}

export async function saveSession(session: Session): Promise<Session> {
  const sessions = await getAllSessions()
  const index = sessions.findIndex((s) => s.id === session.id)
  if (index >= 0) {
    sessions[index] = { ...session, updatedAt: Date.now() }
  } else {
    sessions.push({ ...session, createdAt: session.createdAt ?? Date.now(), updatedAt: Date.now() })
  }
  await localforage.setItem(STORAGE_KEYS.SESSIONS, sessions)
  return sessions[index >= 0 ? index : sessions.length - 1]
}

export async function getSession(id: string): Promise<Session | null> {
  const sessions = await getAllSessions()
  return sessions.find((s) => s.id === id) ?? null
}

export async function getAllSessions(): Promise<Session[]> {
  const sessions = await localforage.getItem<Session[]>(STORAGE_KEYS.SESSIONS)
  return sessions ?? []
}

export async function deleteSession(id: string): Promise<void> {
  const sessions = await getAllSessions()
  const filtered = sessions.filter((s) => s.id !== id)
  await localforage.setItem(STORAGE_KEYS.SESSIONS, filtered)
  await deleteTilesBySessionId(id)
  await deleteAnswersBySessionId(id)
  await deleteHistoryStacksBySessionId(id)
}

export async function saveTile(tile: Tile): Promise<Tile> {
  const tiles = await getAllTiles()
  const index = tiles.findIndex((t) => t.id === tile.id)
  if (index >= 0) {
    tiles[index] = { ...tile, updatedAt: Date.now() }
  } else {
    tiles.push({ ...tile, createdAt: tile.createdAt ?? Date.now(), updatedAt: Date.now() })
  }
  await localforage.setItem(STORAGE_KEYS.TILES, tiles)
  return tiles[index >= 0 ? index : tiles.length - 1]
}

export async function getTile(id: string): Promise<Tile | null> {
  const tiles = await getAllTiles()
  return tiles.find((t) => t.id === id) ?? null
}

export async function getTilesBySessionId(sessionId: string): Promise<Tile[]> {
  const tiles = await getAllTiles()
  return tiles.filter((t) => t.sessionId === sessionId).sort((a, b) => a.index - b.index)
}

export async function getAllTiles(): Promise<Tile[]> {
  const tiles = await localforage.getItem<Tile[]>(STORAGE_KEYS.TILES)
  return tiles ?? []
}

export async function deleteTile(id: string): Promise<void> {
  const tiles = await getAllTiles()
  const filtered = tiles.filter((t) => t.id !== id)
  await localforage.setItem(STORAGE_KEYS.TILES, filtered)
}

export async function deleteTilesBySessionId(sessionId: string): Promise<void> {
  const tiles = await getAllTiles()
  const filtered = tiles.filter((t) => t.sessionId !== sessionId)
  await localforage.setItem(STORAGE_KEYS.TILES, filtered)
}

export async function saveAnswer(answer: Answer): Promise<Answer> {
  const answers = await getAllAnswers()
  const index = answers.findIndex((a) => a.id === answer.id)
  if (index >= 0) {
    answers[index] = { ...answer, updatedAt: Date.now() }
  } else {
    answers.push({ ...answer, createdAt: answer.createdAt ?? Date.now(), updatedAt: Date.now() })
  }
  await localforage.setItem(STORAGE_KEYS.ANSWERS, answers)
  return answers[index >= 0 ? index : answers.length - 1]
}

export async function getAnswer(id: string): Promise<Answer | null> {
  const answers = await getAllAnswers()
  return answers.find((a) => a.id === id) ?? null
}

export async function getAnswersByTileId(tileId: string): Promise<Answer[]> {
  const answers = await getAllAnswers()
  return answers.filter((a) => a.tileId === tileId).sort((a, b) => a.createdAt - b.createdAt)
}

export async function getAnswersBySessionId(sessionId: string): Promise<Answer[]> {
  const answers = await getAllAnswers()
  return answers.filter((a) => a.sessionId === sessionId).sort((a, b) => a.createdAt - b.createdAt)
}

export async function getAllAnswers(): Promise<Answer[]> {
  const answers = await localforage.getItem<Answer[]>(STORAGE_KEYS.ANSWERS)
  return answers ?? []
}

export async function deleteAnswer(id: string): Promise<void> {
  const answers = await getAllAnswers()
  const filtered = answers.filter((a) => a.id !== id)
  await localforage.setItem(STORAGE_KEYS.ANSWERS, filtered)
}

export async function deleteAnswersBySessionId(sessionId: string): Promise<void> {
  const answers = await getAllAnswers()
  const filtered = answers.filter((a) => a.sessionId !== sessionId)
  await localforage.setItem(STORAGE_KEYS.ANSWERS, filtered)
}

export async function saveHistoryStack(stack: HistoryStack): Promise<HistoryStack> {
  const stacks = await getAllHistoryStacks()
  const index = stacks.findIndex((s) => s.id === stack.id)
  if (index >= 0) {
    stacks[index] = { ...stack, updatedAt: Date.now() }
  } else {
    stacks.push({ ...stack, createdAt: stack.createdAt ?? Date.now(), updatedAt: Date.now() })
  }
  await localforage.setItem(STORAGE_KEYS.HISTORY_STACKS, stacks)
  return stacks[index >= 0 ? index : stacks.length - 1]
}

export async function getHistoryStack(id: string): Promise<HistoryStack | null> {
  const stacks = await getAllHistoryStacks()
  return stacks.find((s) => s.id === id) ?? null
}

export async function getHistoryStackByTileId(tileId: string): Promise<HistoryStack | null> {
  const stacks = await getAllHistoryStacks()
  return stacks.find((s) => s.tileId === tileId) ?? null
}

export async function getAllHistoryStacks(): Promise<HistoryStack[]> {
  const stacks = await localforage.getItem<HistoryStack[]>(STORAGE_KEYS.HISTORY_STACKS)
  return stacks ?? []
}

export async function deleteHistoryStack(id: string): Promise<void> {
  const stacks = await getAllHistoryStacks()
  const filtered = stacks.filter((s) => s.id !== id)
  await localforage.setItem(STORAGE_KEYS.HISTORY_STACKS, filtered)
}

export async function deleteHistoryStacksBySessionId(sessionId: string): Promise<void> {
  const stacks = await getAllHistoryStacks()
  const filtered = stacks.filter((s) => s.sessionId !== sessionId)
  await localforage.setItem(STORAGE_KEYS.HISTORY_STACKS, filtered)
}

export async function saveImportJob(job: ImportJob): Promise<ImportJob> {
  const jobs = await getAllImportJobs()
  const index = jobs.findIndex((j) => j.id === job.id)
  if (index >= 0) {
    jobs[index] = { ...job, updatedAt: Date.now() }
  } else {
    jobs.push({ ...job, createdAt: job.createdAt ?? Date.now(), updatedAt: Date.now() })
  }
  await localforage.setItem(STORAGE_KEYS.IMPORT_JOBS, jobs)
  return jobs[index >= 0 ? index : jobs.length - 1]
}

export async function getImportJob(id: string): Promise<ImportJob | null> {
  const jobs = await getAllImportJobs()
  return jobs.find((j) => j.id === id) ?? null
}

export async function getAllImportJobs(): Promise<ImportJob[]> {
  const jobs = await localforage.getItem<ImportJob[]>(STORAGE_KEYS.IMPORT_JOBS)
  return jobs ?? []
}

export async function deleteImportJob(id: string): Promise<void> {
  const jobs = await getAllImportJobs()
  const filtered = jobs.filter((j) => j.id !== id)
  await localforage.setItem(STORAGE_KEYS.IMPORT_JOBS, filtered)
}

export async function saveExportJob(job: ExportJob): Promise<ExportJob> {
  const jobs = await getAllExportJobs()
  const index = jobs.findIndex((j) => j.id === job.id)
  if (index >= 0) {
    jobs[index] = { ...job, updatedAt: Date.now() }
  } else {
    jobs.push({ ...job, createdAt: job.createdAt ?? Date.now(), updatedAt: Date.now() })
  }
  await localforage.setItem(STORAGE_KEYS.EXPORT_JOBS, jobs)
  return jobs[index >= 0 ? index : jobs.length - 1]
}

export async function getExportJob(id: string): Promise<ExportJob | null> {
  const jobs = await getAllExportJobs()
  return jobs.find((j) => j.id === id) ?? null
}

export async function getAllExportJobs(): Promise<ExportJob[]> {
  const jobs = await localforage.getItem<ExportJob[]>(STORAGE_KEYS.EXPORT_JOBS)
  return jobs ?? []
}

export async function saveSyncRecord(record: SyncRecord): Promise<SyncRecord> {
  const records = await getAllSyncRecords()
  const index = records.findIndex((r) => r.id === record.id)
  if (index >= 0) {
    records[index] = record
  } else {
    records.push(record)
  }
  await localforage.setItem(STORAGE_KEYS.SYNC_RECORDS, records)
  return records[index >= 0 ? index : records.length - 1]
}

export async function getSyncRecord(id: string): Promise<SyncRecord | null> {
  const records = await getAllSyncRecords()
  return records.find((r) => r.id === id) ?? null
}

export async function getSyncRecordsBySessionId(sessionId: string): Promise<SyncRecord[]> {
  const records = await getAllSyncRecords()
  return records.filter((r) => r.source === sessionId || r.target === sessionId).sort((a, b) => b.time - a.time)
}

export async function getAllSyncRecords(): Promise<SyncRecord[]> {
  const records = await localforage.getItem<SyncRecord[]>(STORAGE_KEYS.SYNC_RECORDS)
  return records ?? []
}

export async function deleteSyncRecord(id: string): Promise<void> {
  const records = await getAllSyncRecords()
  const filtered = records.filter((r) => r.id !== id)
  await localforage.setItem(STORAGE_KEYS.SYNC_RECORDS, filtered)
}

export async function saveAutoSyncSettings(settings: AutoSyncSettings): Promise<AutoSyncSettings> {
  await localforage.setItem(STORAGE_KEYS.AUTO_SYNC_SETTINGS, settings)
  return settings
}

export async function getAutoSyncSettings(): Promise<AutoSyncSettings> {
  const settings = await localforage.getItem<AutoSyncSettings>(STORAGE_KEYS.AUTO_SYNC_SETTINGS)
  return settings ?? { enabled: false, intervalMs: 300000 } // 5 min default
}

export async function deleteAutoSyncSettings(): Promise<void> {
  await localforage.removeItem(STORAGE_KEYS.AUTO_SYNC_SETTINGS)
}

export async function getHermesGatewayUrl(): Promise<string | null> {
  return (await localforage.getItem<string>(STORAGE_KEYS.HERMES_GATEWAY_URL)) ?? null
}

export async function saveHermesGatewayUrl(url: string): Promise<string> {
  await localforage.setItem(STORAGE_KEYS.HERMES_GATEWAY_URL, url)
  return url
}

export async function deleteExportJob(id: string): Promise<void> {
  const jobs = await getAllExportJobs()
  const filtered = jobs.filter((j) => j.id !== id)
  await localforage.setItem(STORAGE_KEYS.EXPORT_JOBS, filtered)
}

export async function getLastSessionId(): Promise<string | null> {
  return (await localforage.getItem<string>(STORAGE_KEYS.LAST_SESSION_ID)) ?? null
}

export async function setLastSessionId(sessionId: string): Promise<void> {
  await localforage.setItem(STORAGE_KEYS.LAST_SESSION_ID, sessionId)
}

export async function getResumeSession(): Promise<Session | null> {
  const lastSessionId = await getLastSessionId()
  if (!lastSessionId) return null
  const session = await getSession(lastSessionId)
  if (session && (session.status === 'active' || session.status === 'paused')) {
    return session
  }
  const sessions = await getAllSessions()
  const activeSessions = sessions
    .filter((s) => s.status === 'active' || s.status === 'paused')
    .sort((a, b) => b.updatedAt - a.updatedAt)
  return activeSessions[0] ?? null
}

export async function exportSessionToJson(sessionId: string): Promise<string> {
  const session = await getSession(sessionId)
  if (!session) throw new Error('Session not found')

  const tiles = await getTilesBySessionId(sessionId)
  const answers = await getAnswersBySessionId(sessionId)
  const historyStacks = await getAllHistoryStacks().then((stacks) =>
    stacks.filter((s) => s.sessionId === sessionId)
  )
  const syncRecords = await getSyncRecordsBySessionId(sessionId)

  const exportData = {
    version: CURRENT_MIGRATION_VERSION,
    exportedAt: Date.now(),
    session,
    tiles,
    answers,
    historyStacks,
    syncRecords,
  }

  return JSON.stringify(exportData, null, 2)
}

export async function exportAllToJson(): Promise<string> {
  const sessions = await getAllSessions()
  const tiles = await getAllTiles()
  const answers = await getAllAnswers()
  const historyStacks = await getAllHistoryStacks()
  const importJobs = await getAllImportJobs()
  const exportJobs = await getAllExportJobs()
  const syncRecords = await getAllSyncRecords()
  const autoSyncSettings = await getAutoSyncSettings()

  const exportData = {
    version: CURRENT_MIGRATION_VERSION,
    exportedAt: Date.now(),
    sessions,
    tiles,
    answers,
    historyStacks,
    importJobs,
    exportJobs,
    syncRecords,
    autoSyncSettings,
  }

  return JSON.stringify(exportData, null, 2)
}

export interface ImportData {
  version: number
  exportedAt: number
  session?: Session
  tiles?: Tile[]
  answers?: Answer[]
  historyStacks?: HistoryStack[]
  sessions?: Session[]
  importJobs?: ImportJob[]
  exportJobs?: ExportJob[]
  syncRecords?: SyncRecord[]
  autoSyncSettings?: AutoSyncSettings
}

export async function importFromJson(json: string): Promise<{ success: boolean; errors: string[] }> {
  const errors: string[] = []

  try {
    const data: ImportData = JSON.parse(json)

    if (data.version !== CURRENT_MIGRATION_VERSION) {
      errors.push(`Version mismatch: expected ${CURRENT_MIGRATION_VERSION}, got ${data.version}`)
    }

    if (data.session) {
      await saveSession(data.session)
      if (data.tiles) {
        for (const tile of data.tiles) {
          await saveTile(tile)
        }
      }
      if (data.answers) {
        for (const answer of data.answers) {
          await saveAnswer(answer)
        }
      }
      if (data.historyStacks) {
        for (const stack of data.historyStacks) {
          await saveHistoryStack(stack)
        }
      }
    }

    if (data.sessions) {
      for (const session of data.sessions) {
        await saveSession(session)
      }
    }
    if (data.tiles) {
      for (const tile of data.tiles) {
        await saveTile(tile)
      }
    }
    if (data.answers) {
      for (const answer of data.answers) {
        await saveAnswer(answer)
      }
    }
    if (data.historyStacks) {
      for (const stack of data.historyStacks) {
        await saveHistoryStack(stack)
      }
    }
    if (data.importJobs) {
      for (const job of data.importJobs) {
        await saveImportJob(job)
      }
    }
    if (data.exportJobs) {
      for (const job of data.exportJobs) {
        await saveExportJob(job)
      }
    }
    if (data.syncRecords) {
      for (const record of data.syncRecords) {
        await saveSyncRecord(record)
      }
    }
    if (data.autoSyncSettings) {
      await saveAutoSyncSettings(data.autoSyncSettings)
    }

    return { success: errors.length === 0, errors }
  } catch (error) {
    errors.push(`Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    return { success: false, errors }
  }
}

export async function clearAllData(): Promise<void> {
  await localforage.clear()
}

export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`
}

export function generateSessionId(): string {
  return `session-${generateId()}`
}

export function generateTileId(): string {
  return `tile-${generateId()}`
}

export function generateAnswerId(): string {
  return `answer-${generateId()}`
}

export function generateHistoryStackId(): string {
  return `history-${generateId()}`
}

export function generateImportJobId(): string {
  return `import-${generateId()}`
}

export function generateExportJobId(): string {
  return `export-${generateId()}`
}

export function createEmptySession(taskId: string, title: string, totalSteps: number): Session {
  const now = Date.now()
  return {
    id: generateSessionId(),
    taskId,
    title,
    activeIndex: 0,
    status: 'active',
    syncState: 'pending',
    createdAt: now,
    updatedAt: now,
    totalSteps,
    currentStep: 1,
    metadata: {},
  }
}

export function createEmptyTile(sessionId: string, index: number, question: string, suggestions: string[]): Tile {
  const now = Date.now()
  return {
    id: generateTileId(),
    sessionId,
    index,
    question,
    suggestions,
    status: index === 0 ? 'active' : 'pending',
    safetyLevel: 'safe',
    createdAt: now,
    updatedAt: now,
    isRecalculating: false,
    dependsOnPrevious: index > 0,
    metadata: {},
  }
}

export function createEmptyAnswer(tileId: string, sessionId: string, value: string, type: AnswerType): Answer {
  const now = Date.now()
  return {
    id: generateAnswerId(),
    tileId,
    sessionId,
    value,
    type,
    isRecalculated: false,
    createdAt: now,
    updatedAt: now,
  }
}

export function createEmptyHistoryStack(sessionId: string, tileId: string): HistoryStack {
  const now = Date.now()
  return {
    id: generateHistoryStackId(),
    sessionId,
    tileId,
    answers: [],
    currentIndex: -1,
    maxSize: 50,
    createdAt: now,
    updatedAt: now,
  }
}

export function createEmptyImportJob(source: ImportSource, sourceData: string): ImportJob {
  const now = Date.now()
  return {
    id: generateImportJobId(),
    source,
    sourceData,
    status: 'pending',
    parsedTasks: [],
    errors: [],
    createdAt: now,
    updatedAt: now,
  }
}

export function createEmptyExportJob(sessionId: string, format: 'json' | 'markdown'): ExportJob {
  const now = Date.now()
  return {
    id: generateExportJobId(),
    sessionId,
    format,
    data: '',
    status: 'pending',
    createdAt: now,
    updatedAt: now,
  }
}